import type { LocationQuery, RouteLocationRaw } from 'vue-router';
import { userHomeRoute } from '@/utils/portalAccess';

export interface PortalGuardTarget {
  path: string;
  name?: unknown;
  query?: LocationQuery;
  meta: { requiresGuardian?: unknown };
}

export interface PortalGuardState {
  /** `__typename` of the signed-in account: 'User' (staff/guardian) or 'Student'. */
  userType: string | undefined;
  isGuardianOnly: boolean;
  /** Has a guardian portal or profile in the current school. */
  hasGuardianAccess: boolean;
  /** Where a student lands (CBT-only schools differ). */
  studentLanding: string;
}

/**
 * Keeps each account type inside its own portal. Returns the redirect for a
 * cross-portal visit, or null when the navigation may continue.
 *
 * The guardian portal is identified by `meta.requiresGuardian` (set on the
 * /parent route tree) rather than the path, so the public /parent/register
 * and /parent/accept-invitation pages stay reachable.
 */
export function portalRedirect(to: PortalGuardTarget, state: PortalGuardState): RouteLocationRaw | null {
  const { userType } = state;
  if (!userType) return null;
  const isAdminPath = to.path.startsWith('/admin');
  const isStudentPath = to.path.startsWith('/student');
  const isGuardianRoute = !!to.meta.requiresGuardian;

  if (userType === 'Student') {
    if (isAdminPath || isGuardianRoute) return { name: state.studentLanding };
    return null;
  }

  if (userType === 'User') {
    // Payment gateways return every payer to the student payment-status URL;
    // a staff/guardian payer is a parent paying a child's bill, so verify it
    // on the parent page with the same order reference.
    if (to.name === 'student-payment-status') return { name: 'parent-payment-status', query: to.query ?? {} };
    if (isAdminPath && state.isGuardianOnly) return { name: 'parent-home' };
    if (isStudentPath) return { name: userHomeRoute(state.isGuardianOnly) };
    if (isGuardianRoute && !state.hasGuardianAccess) return { name: 'admin-home' };
  }

  return null;
}
